import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import { Lock, BookmarkCheck, Sparkles, Languages, X } from "lucide-react";

// Sections whose /:slug detail pages count as "content" toward the free limit.
const SECTIONS = new Set([
  "kathas", "mantras", "suktas", "stotras", "puja-vidhi", "festivals", "vedic-knowledge",
  "deities", "temples", "learn", "journal", "scriptures", "rishis", "philosophy",
  "pilgrimage", "samskaras", "sanskrit",
]);
const KEY = "dharmartha_viewed_content";
const DEFAULT_FREE_LIMIT = 5;

export default function ContentGate() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { pick, lang } = useLang();
  const { system_messages } = useSettings();
  const sm = (system_messages || {}).gate_content || {};
  const enabled = sm.enabled !== false;
  const FREE_LIMIT = Number.isFinite(sm.free_limit) ? sm.free_limit : DEFAULT_FREE_LIMIT;
  const [gated, setGated] = useState(false);

  useEffect(() => {
    // user: null = still checking, false = anonymous, object = signed in.
    if (!enabled) { setGated(false); return; }
    if (user !== false) { setGated(false); return; }
    if (sessionStorage.getItem("dharmartha_gate_dismissed")) { setGated(false); return; }
    const parts = pathname.split("/").filter(Boolean);
    const isDetail = parts.length === 2 && SECTIONS.has(parts[0]);
    if (!isDetail) { setGated(false); return; }
    let viewed = [];
    try { viewed = JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { viewed = []; }
    if (!viewed.includes(pathname)) {
      viewed = [...viewed, pathname];
      localStorage.setItem(KEY, JSON.stringify(viewed));
    }
    setGated(viewed.length > FREE_LIMIT);
  }, [pathname, user, enabled, FREE_LIMIT]);

  useEffect(() => {
    document.body.style.overflow = gated ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [gated]);

  if (!gated || user) return null;

  const benefits = [
    { Icon: BookmarkCheck, text: pick("असीमित पठन एवं संचयन", "Unlimited reading & bookmarks") },
    { Icon: Sparkles, text: pick("जप एवं साधना ट्रैकिंग", "Japa & sadhana tracking") },
    { Icon: Languages, text: pick("हिन्दी एवं अंग्रेज़ी में पढ़ें", "Read in Hindi & English") },
  ];

  return createPortal(
    <div className="fixed inset-0 z-[300] flex items-end justify-center bg-[rgba(28,23,16,0.55)] backdrop-blur-md sm:items-center" data-testid="content-gate">
      <div className="relative w-full max-w-lg border border-line bg-[var(--parchment)] p-8 shadow-2xl sm:m-5 md:p-10">
        <button
          data-testid="content-gate-close"
          aria-label={pick("बंद करें", "Close")}
          onClick={() => { sessionStorage.setItem("dharmartha_gate_dismissed", "1"); setGated(false); }}
          className="absolute right-4 top-4 text-ink-3 transition-colors hover:text-ink"
        >
          <X className="h-5 w-5" />
        </button>
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-[var(--gold-muted)]/50 bg-[rgba(176,141,87,0.1)]">
          <Lock className="h-5 w-5 text-gold" />
        </span>
        <p className="overline-label mt-5 text-gold">{pick("निःशुल्क खाता", "Free account")}</p>
        <h2 className="mt-2 font-serif-latin text-3xl text-ink md:text-4xl">
          {pick(sm.title_hi || "आगे पढ़ने हेतु साइन इन करें", sm.title_en || "Sign in to keep reading")}
        </h2>
        <p className="mt-3 leading-relaxed text-ink-2">
          {pick(
            sm.body_hi || "आपने पाँच निःशुल्क प्रविष्टियाँ पढ़ ली हैं। धर्मार्थ का समस्त ज्ञान — कथाएँ, मंत्र, स्तोत्र, देवता एवं मंदिर — नि:शुल्क पढ़ते रहने हेतु एक निःशुल्क खाता बनाएँ।",
            sm.body_en || "You've read your free entries. Create a free account to keep exploring all of Dharmartha — kathas, mantras, stotras, deities and temples — at no cost."
          )}
        </p>
        <ul className="mt-6 space-y-2.5">
          {benefits.map(({ Icon, text }, i) => (
            <li key={i} className="flex items-center gap-3 text-sm text-ink-2">
              <Icon className="h-4 w-4 shrink-0 text-saffron" /> {text}
            </li>
          ))}
        </ul>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <Link to="/register" data-testid="content-gate-signup" className="inline-flex flex-1 items-center justify-center bg-[var(--saffron)] px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)]">
            {pick("निःशुल्क खाता बनाएँ", "Create free account")}
          </Link>
          <Link to="/login" data-testid="content-gate-login" className="inline-flex flex-1 items-center justify-center border border-[var(--ink)] px-6 py-3 text-sm text-ink transition-colors hover:bg-ink hover:text-[var(--surface)]">
            {pick("साइन इन", "Log in")}
          </Link>
        </div>
        <Link to="/" className="mt-5 block text-center text-xs text-ink-3 underline-offset-2 hover:underline" data-testid="content-gate-home">
          {pick("मुख्य पृष्ठ पर लौटें", "Back to home")}
        </Link>
      </div>
    </div>,
    document.body
  );
}
